import React, { forwardRef, memo } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { selectCurrentLanguage } from "../../../store/slices/languageSlice";
import Utils from "../../../../utils/Utils";
import Buttons from "../../button/Buttons";

const EMBED_REGEX =
  /(?:youtube\.com\/(?:embed\/|v\/|watch\?v=)|youtu\.be\/|vimeo\.com\/)[\w-]+/;

const VideoBackground = forwardRef((props, ref) => {
  const { parent } = props;
  const lang = useSelector(selectCurrentLanguage);
  const cl = Utils.isValidJson(parent.className)
    ? JSON.parse(parent.className)
    : parent.className;

  const src = parent?.content?.video_source;
  const opacity = parent?.content?.opacity ?? 0.5;
  const overlayRaw = parent?.content?.overlay;
  const overlay = Utils.isValidJson(overlayRaw)
    ? JSON.parse(overlayRaw)
    : overlayRaw;

  const overlayStyle = overlay
    ? {
        background: Array.isArray(overlay)
          ? `linear-gradient(to right top, ${overlay.join(", ")})`
          : overlay,
        opacity,
      }
    : null;

  const poster = parent?.image_main
    ? Utils.formatURL(
        `posts/${parent?.post_content_id}/${parent.image_main}?s=md`,
      )
    : undefined;

  const isEmbed = src && EMBED_REGEX.test(src);

  return (
    <section ref={ref} className="relative overflow-hidden" style={{ ...cl }}>
      {overlayStyle && (
        <div
          className="overlay z-[1] absolute h-full w-full top-0 left-0"
          style={overlayStyle}
        />
      )}

      <Container className="relative z-[2]">
        <Row className="justify-center">
          <Col
            lg={8}
            xl={7}
            md={10}
            xs={12}
            className="text-center py-[160px] lg:py-[120px] md:py-[95px] sm:py-[80px] xs:py-[50px]"
          >
            {parent?.title_lang?.[lang] && (
              <h3 className="mb-[35px] font-semibold font-serif text-white">
                {parent.title_lang[lang]}
              </h3>
            )}
            {parent?.subtitle_widget?.[lang] && (
              <p className="w-[80%] mb-[45px] opacity-70 leading-[32px] text-lg font-serif text-white mx-auto sm:w-full">
                {parent.subtitle_widget[lang]}
              </p>
            )}
            {parent?.content?.link?.[lang] && (
              <Buttons
                ariaLabel="video background button"
                href={parent.content.link[lang]}
                className="btn-fill mx-3 drop-shadow-md	font-medium font-serif uppercase rounded-[50px] text-[15px] md:mb-[15px] xs:mb-0"
                size="lg"
                themeColor="#fff"
                color="#000000"
                title={parent?.content?.link_title_lang?.[lang] || ""}
              />
            )}
          </Col>
        </Row>
      </Container>

      <div className="absolute top-1/2 left-1/2 w-full h-full -translate-y-1/2 -translate-x-1/2 xl:w-[120%] lg:w-[130%] xs:w-[200%]">
        <div className="fit-video">
          {isEmbed ? (
            <iframe
              title="Video Background"
              src={src}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video loop autoPlay muted poster={poster}>
              <source type="video/mp4" src={src} />
              <source type="video/webm" src={src} />
            </video>
          )}
        </div>
      </div>
    </section>
  );
});

export default memo(VideoBackground);
